// src/views/AccommodationProvider/ConfigureFloors.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ConfigureFloors = () => {
  const navigate = useNavigate();
  const { propertyId, blockId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  const [property, setProperty] = useState(null);
  const [block, setBlock] = useState(null);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (property && block) {
      return `Configure Floors - Block ${block.block_number}`;
    }
    return 'Configure Floors';
  };

  const getSubtitleMessage = () => {
    if (property) {
      return property.trading_name || `Property #${property.id}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  // Fetch property, block, and floors data
  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId || !blockId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        if (propertyResponse.status === 200) {
          setProperty(propertyResponse.data);
        }

        // Fetch block details
        const blockResponse = await axiosInstance.get(`/api/blocks/${blockId}/`);
        if (blockResponse.status === 200) {
          const blockData = blockResponse.data;
          setBlock(blockData);
          
          // Check if block is configured
          if (!blockData.is_configured) {
            setAlert({
              type: 'warning',
              message: 'This block needs to be configured first before you can configure floors.',
              visible: true
            });
            setLoading(false);
            return;
          }
          
          // Fetch floors from API
          try {
            const floorsResponse = await axiosInstance.get(`/api/blocks/${blockId}/floors/`);
            if (floorsResponse.status === 200) {
              setFloors(floorsResponse.data);
            }
          } catch (floorError) {
            console.error('Error fetching floors:', floorError);
            setFloors([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load floors. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, blockId, regStatus]);

  const handleBackToBlocks = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}`);
  };

  const handleConfigureBlock = () => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/maintain/${blockId}`);
  };

  const handleViewFloor = (floorId) => {
    // TODO: Implement view floor functionality
    console.log(`View floor ${floorId}`);
  };

  const handleMaintainFloor = (floorId) => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors/${floorId}/maintain`);
  };

  const handleConfigureRooms = (floorId) => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}/block/${blockId}/configure-floors/${floorId}/configure-rooms`);
  };

  // If block is not configured, show call to action
  if (!loading && block && !block.is_configured) {
    return (
      <div className="container-fluid py-4">
        {/* Header Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 className="mb-1">{getWelcomeMessage()}</h2>
                {getSubtitleMessage() && (
                  <p className="text-muted mb-1">{getSubtitleMessage()}</p>
                )}
                <Badge color="info" className="me-2">
                  {getProviderTypeDisplay()}
                </Badge>
                <Badge color="secondary">
                  Block {block.block_number}
                </Badge>
              </div>
              <Button 
                color="outline-secondary" 
                onClick={handleBackToBlocks}
                className="btn-sm"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Blocks
              </Button>
            </div>
          </div>
        </div>

        {/* Alert */}
        {alert.visible && (
          <Row className="mb-3">
            <Col>
              <Alert 
                color={alert.type}
                toggle={() => setAlert({ ...alert, visible: false })}
              >
                {alert.message}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Call to Action Card */}
        <Row>
          <Col md="12">
            <Card className="border-warning">
              <CardBody className="text-center py-5">
                <i className="bi bi-exclamation-triangle display-1 text-warning mb-4"></i>
                <h3 className="mb-3">Block Not Configured</h3>
                <p className="text-muted mb-4">
                  Before you can configure floors, you need to configure this block first.<br/>
                  Please provide the block details including name, occupation date, gender designation, and number of floors.
                </p>
                <Button 
                  color="primary" 
                  size="lg"
                  onClick={handleConfigureBlock}
                >
                  <i className="bi bi-gear me-2"></i>
                  Configure Block Now
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              {block && (
                <Badge color="secondary">
                  Block {block.block_number} - {block.block_name}
                </Badge>
              )}
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToBlocks}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Blocks
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Floors Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-layers me-2 text-primary"></i>
                Block Floors
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading floors...</p>
                </div>
              ) : (
                <>
                  {floors.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-layers-half display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Floors Found</h5>
                      <p className="text-muted">
                        This block does not have any floors configured yet. Please go to relevant block and click maintain to add floors to this block.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ paddingBottom: '150px' }}>
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Floor</th>
                            <th>Gender</th>
                            <th>No Of Single Rooms</th>
                            <th>No Of Shared Rooms</th>
                            <th>No Of Rooms</th>
                            <th>Total Beds</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {floors.map((floor) => {
                            const totalRooms = floor.is_configured 
                              ? (parseInt(floor.no_of_single_rooms || 0) + parseInt(floor.no_of_shared_rooms || 0))
                              : null;
                            
                            return (
                            <tr key={floor.id}>
                              <td>
                                <div className="fw-bold">
                                  {block?.block_number}{floor.floor_number}
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {floor.gender ? floor.gender.charAt(0).toUpperCase() + floor.gender.slice(1) : 'Not Set'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted">{floor.no_of_single_rooms || 'Not Set'}</span>
                              </td>
                              <td>
                                <span className="text-muted">{floor.no_of_shared_rooms || 'Not Set'}</span>
                              </td>
                              <td>
                                <span className="text-muted">{totalRooms !== null ? totalRooms : 'Not Set'}</span>
                              </td>
                              <td>
                                <span className="text-muted">{floor.total_beds || 'Not Set'}</span>
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="light" 
                                    size="sm" 
                                    className="btn-outline-secondary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleViewFloor(floor.id)}
                                    >
                                      <i className="bi bi-eye me-2"></i>
                                      View
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleMaintainFloor(floor.id)}
                                    >
                                      <i className="bi bi-gear me-2"></i>
                                      Configure Floor
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleConfigureRooms(floor.id)}
                                    >
                                      <i className="bi bi-door-closed me-2"></i>
                                      Configure Rooms
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Action Summary */}
      {floors.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="border-0 bg-light">
              <CardBody>
                <CardTitle tag="h6" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Floor Management Actions
                </CardTitle>
                <Row>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>View:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View floor details and information</li>
                      <li>See current floor configuration</li>
                      <li>Review floor rooms and layout</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Configure Floor:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>Edit floor information</li>
                      <li>Update floor settings</li>
                      <li>Manage floor configuration</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Configure Rooms:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View all rooms on this floor</li>
                      <li>Manage room assignments</li>
                      <li>Edit individual room details</li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ConfigureFloors;
